import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { formatPrice, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ImageUpload from "@/components/ImageUpload";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const themes = [
  { value: "blue", label: "Albastru" },
  { value: "pink", label: "Roz" },
  { value: "amber", label: "Portocaliu" },
];

const emptyForm = {
  fragrance: "", title: "", subtitle: "", description: "",
  price: "", old_price: "", image: "", theme: "blue", features: "", order: "99",
};

export default function AdminOffers() {
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [pageTitle, setPageTitle] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  const [pageSubtitle, setPageSubtitle] = useState("");
  const [savingSubtitle, setSavingSubtitle] = useState(false);

  const load = () => api.get("/offers").then((res) => setOffers(res.data));
  useEffect(() => {
    load();
    api.get("/settings").then((res) => {
      setPageTitle(res.data?.offers_title || "Pachete 2+1 GRATIS");
      setPageSubtitle(res.data?.offers_subtitle || "");
    });
  }, []);

  const saveTitle = async () => {
    if (!pageTitle.trim()) { toast.error("Titlul nu poate fi gol"); return; }
    setSavingTitle(true);
    try {
      await api.put("/settings/offers-title", { title: pageTitle.trim() });
      toast.success("Titlul paginii a fost actualizat");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingTitle(false);
    }
  };

  const saveSubtitle = async () => {
    if (!pageSubtitle.trim()) { toast.error("Subtitlul nu poate fi gol"); return; }
    setSavingSubtitle(true);
    try {
      await api.put("/settings/offers-subtitle", { subtitle: pageSubtitle.trim() });
      toast.success("Subtitlul paginii a fost actualizat");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingSubtitle(false);
    }
  };

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (o) => {
    setEditing(o);
    setForm({
      ...o,
      old_price: o.old_price ?? "",
      order: String(o.order ?? 99),
      features: (o.features || []).join(", "),
    });
    setOpen(true);
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title || !form.subtitle || !form.price || !form.image) {
      toast.error("Completează titlul, subtitlul, prețul și imaginea");
      return;
    }
    setSaving(true);
    const payload = {
      fragrance: form.fragrance || "",
      title: form.title,
      subtitle: form.subtitle,
      description: form.description || "",
      price: parseFloat(form.price),
      old_price: form.old_price ? parseFloat(form.old_price) : null,
      image: form.image,
      theme: form.theme,
      features: form.features ? form.features.split(",").map((s) => s.trim()).filter(Boolean) : [],
      order: parseInt(form.order || "99", 10),
    };
    try {
      if (editing) await api.put(`/offers/${editing._id}`, payload);
      else await api.post("/offers", payload);
      toast.success(editing ? "Ofertă actualizată" : "Ofertă adăugată");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/offers/${deleteId}`);
      toast.success("Ofertă ștearsă");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-offers">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Oferte</h1>
          <p className="mt-1 text-slate-500">{offers.length} oferte active</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-sky-600 hover:bg-sky-500" data-testid="admin-add-offer">
          <Plus className="mr-1.5 h-4 w-4" /> Adaugă ofertă
        </Button>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="offers-title-editor">
        <Label>Titlul paginii Oferte</Label>
        <p className="mt-0.5 text-sm text-slate-500">Textul mare afișat în capul paginii publice „Oferte" (ex. „Pachete 2+1 GRATIS").</p>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
          <Input
            value={pageTitle}
            onChange={(e) => setPageTitle(e.target.value)}
            className="flex-1"
            placeholder="Pachete 2+1 GRATIS"
            data-testid="offers-title-input"
          />
          <Button onClick={saveTitle} disabled={savingTitle} className="rounded-full bg-sky-600 hover:bg-sky-500 sm:w-auto" data-testid="offers-title-save">
            {savingTitle ? "Se salvează..." : "Salvează titlul"}
          </Button>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="offers-subtitle-editor">
        <Label>Subtitlul paginii Oferte</Label>
        <p className="mt-0.5 text-sm text-slate-500">Textul descriptiv afișat sub titlul paginii publice „Oferte".</p>
        <div className="mt-3 flex flex-col gap-3">
          <Textarea
            value={pageSubtitle}
            onChange={(e) => setPageSubtitle(e.target.value)}
            rows={3}
            placeholder="La fiecare 2 detergenți gel premium cumpărați, primești 1 balsam de rufe complet gratuit..."
            data-testid="offers-subtitle-input"
          />
          <Button onClick={saveSubtitle} disabled={savingSubtitle} className="self-start rounded-full bg-sky-600 hover:bg-sky-500" data-testid="offers-subtitle-save">
            {savingSubtitle ? "Se salvează..." : "Salvează subtitlul"}
          </Button>
        </div>
      </div>

      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {offers.map((o) => (
          <div key={o._id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`admin-offer-${o._id}`}>
            <div className="aspect-video overflow-hidden bg-slate-50">
              <img src={o.image} alt={o.title} className="h-full w-full object-contain" />
            </div>
            <div className="p-4">
              <p className="label-caps text-sky-600">{o.fragrance}</p>
              <h3 className="mt-1 font-display font-bold text-slate-900">{o.title}</h3>
              <div className="mt-2 flex items-center gap-2">
                <span className="font-display text-lg font-black text-slate-900">{formatPrice(o.price)}</span>
                {o.old_price && <span className="text-sm text-slate-400 line-through">{formatPrice(o.old_price)}</span>}
              </div>
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(o)} data-testid={`admin-offer-edit-${o._id}`}>
                  <Pencil className="mr-1.5 h-3.5 w-3.5" /> Editează
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => setDeleteId(o._id)} data-testid={`admin-offer-delete-${o._id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="admin-offer-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editează ofertă" : "Adaugă ofertă"}</DialogTitle>
            <DialogDescription>Completează detaliile pachetului promoțional.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Imagine ofertă *</Label>
              <div className="mt-1.5">
                <ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testId="offer-image-upload" />
              </div>
            </div>
            <div>
              <Label>Titlu *</Label>
              <Input value={form.title} onChange={update("title")} className="mt-1.5" placeholder="Pachet 2+1 GRATIS · ..." data-testid="offer-form-title" />
            </div>
            <div>
              <Label>Subtitlu *</Label>
              <Input value={form.subtitle} onChange={update("subtitle")} className="mt-1.5" placeholder="2 detergenți + 1 balsam ..." data-testid="offer-form-subtitle" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Parfum</Label>
                <Input value={form.fragrance} onChange={update("fragrance")} className="mt-1.5" data-testid="offer-form-fragrance" />
              </div>
              <div>
                <Label>Temă culoare</Label>
                <Select value={form.theme} onValueChange={(v) => setForm((f) => ({ ...f, theme: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="offer-form-theme"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {themes.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Preț (lei) *</Label>
                <Input type="number" value={form.price} onChange={update("price")} className="mt-1.5" data-testid="offer-form-price" />
              </div>
              <div>
                <Label>Preț vechi</Label>
                <Input type="number" value={form.old_price} onChange={update("old_price")} className="mt-1.5" data-testid="offer-form-oldprice" />
              </div>
              <div>
                <Label>Ordine</Label>
                <Input type="number" value={form.order} onChange={update("order")} className="mt-1.5" data-testid="offer-form-order" />
              </div>
            </div>
            <div>
              <Label>Descriere</Label>
              <Textarea value={form.description} onChange={update("description")} className="mt-1.5" rows={2} data-testid="offer-form-description" />
            </div>
            <div>
              <Label>Beneficii (separate prin virgulă)</Label>
              <Input value={form.features} onChange={update("features")} className="mt-1.5" placeholder="2 detergenți 4L, 1 balsam GRATIS, ..." data-testid="offer-form-features" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
            <Button onClick={save} disabled={saving} className="bg-sky-600 hover:bg-sky-500" data-testid="offer-form-save">
              {saving ? "Se salvează..." : "Salvează"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ștergi oferta?</AlertDialogTitle>
            <AlertDialogDescription>Această acțiune nu poate fi anulată.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-500" data-testid="confirm-delete-offer">Șterge</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
